import { useCallback, useEffect, useMemo, type Dispatch, type SetStateAction } from "react";

import { getLocalStorageKey, type LocalStorageKey } from "@/storage/local-storage";
import { useLocalStorage as useReactUseLocalStorage } from "react-use";

type StringEnum = Record<string, string>;
type EnumValue<TEnum extends StringEnum> = TEnum[keyof TEnum];

const useLocalStorage = <TEnum extends StringEnum>(
  key: LocalStorageKey,
  initialValue: EnumValue<TEnum>,
  enumType: TEnum,
): [EnumValue<TEnum>, Dispatch<SetStateAction<EnumValue<TEnum>>>] => {
  const storageKey = getLocalStorageKey(key);
  const validValues = useMemo(() => Object.values(enumType) as EnumValue<TEnum>[], [enumType]);
  const [storedValue, setStoredValue] = useReactUseLocalStorage<EnumValue<TEnum>>(
    storageKey,
    initialValue,
    { raw: true },
  );

  const isValidValue = useCallback(
    (value: unknown): value is EnumValue<TEnum> =>
      typeof value === "string" && validValues.includes(value as EnumValue<TEnum>),
    [validValues],
  );

  const value = isValidValue(storedValue) ? storedValue : initialValue;

  useEffect(() => {
    if (!isValidValue(storedValue)) {
      setStoredValue(initialValue);
    }
  }, [initialValue, isValidValue, setStoredValue, storedValue]);

  const setValue = useCallback<Dispatch<SetStateAction<EnumValue<TEnum>>>>(
    (nextValue) => {
      const resolvedValue = typeof nextValue === "function" ? nextValue(value) : nextValue;

      setStoredValue(resolvedValue);
    },
    [setStoredValue, value],
  );

  return [value, setValue];
};

export default useLocalStorage;
